import React, { useState } from "react";
import { useDevice } from "../../context/DeviceModalContext";
import DSSwitch from "../../../system-design/DSSwitch";
import DSBox from "../../../system-design/DSBox";
import DSCard from "../../../system-design/DSCard";
import DSTypography from "../../../system-design/DSTypography";
import DSCardContent from "../../../system-design/DSCardContent";
import DSFormControlLabel from "../../../system-design/DSFormControlLabel";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../../system-design/sectionCardSx";

// 1000 ECO hours ≈ 15 kWh saved ≈ 61 Tree-Days, so each ECO hour ≈ 0.061 Tree-Days.
const TREE_DAYS_PER_ECO_HOUR = 61 / 1000;

// Firmware 641.7 changed what the clock runs with while `ecoModeEnabled` is
// unset from ON to OFF (clock-application-v3 #603).
const ECO_DEFAULT_OFF_MIN_VERSION = 641.7;

/**
 * ECO Mode on/off toggle + environmental-impact readout for a single device.
 *
 * Writes the `ecoModeEnabled` synced setting (BIDIRECTIONAL) which the clock picks up;
 * unset shows what the clock runs with: ON below firmware 641.7, OFF from it. Reads the device-reported lifetime `ecoHours`
 * and converts it to "Tree-Days" (a Tree-Day ≈ the CO₂ a mature tree absorbs in a day).
 */
const DeviceEcoModeCard: React.FC = () => {
  const { device, updateConfig } = useDevice();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const unsetDefault =
    !device?.version || device.version < ECO_DEFAULT_OFF_MIN_VERSION;
  const enabled = device?.ecoModeEnabled?.value ?? unsetDefault;

  // Lifetime ECO hours reported by the device → Tree-Days (1 decimal, trailing .0 dropped).
  const ecoHours = device?.ecoHours?.value ?? 0;
  const treeDays = parseFloat((ecoHours * TREE_DAYS_PER_ECO_HOUR).toFixed(1));

  const handleChange = async (checked: boolean) => {
    setLoading(true);
    setError("");
    try {
      await updateConfig("ecoModeEnabled", checked);
    } catch (e) {
      setError(
        `Failed to update ECO Mode: ${(e as Error).message || "Unknown error"}`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <DSCard className="card-stats" sx={[sectionCardSx, { mb: 2 }]}>
      <DSSectionHeading>ECO Mode</DSSectionHeading>
      <DSCardContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
            {"Save energy when Klydoclock isn't being used"}
          </DSTypography>

          <DSFormControlLabel
            control={
              <DSSwitch
                checked={enabled}
                onChange={(e) => handleChange(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <DSTypography variant="body1">
                Automatic ECO Mode {enabled ? "On" : "Off"}
              </DSTypography>
            }
          />

          {error && (
            <DSTypography variant="body2" color="error" sx={{ ml: 4 }}>
              {error}
            </DSTypography>
          )}

          {enabled ? (
            <DSBox>
              <DSTypography variant="h6">🌳 {treeDays} Tree-Days</DSTypography>
              <DSTypography variant="body2">
                of environmental impact
              </DSTypography>
              <DSTypography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
              >
                *A Tree-Day is the amount of CO₂ one mature tree absorbs in one
                day.
              </DSTypography>
            </DSBox>
          ) : (
            <DSBox>
              <DSTypography variant="body1">
                {`You've accumulated ${treeDays} Tree-Days`}
              </DSTypography>
              <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
                Turn ECO back on to keep growing your impact.
              </DSTypography>
            </DSBox>
          )}
        </DSBox>
      </DSCardContent>
    </DSCard>
  );
};

export default DeviceEcoModeCard;
